import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './locations-page.style.js';
import { getLocationsByCompany, deleteLocation } from '@cmms/api';
import { Firestore } from 'firebase/firestore';
import { createLocationsTableConfig } from './locations-table-config.js';


interface Location {
  id: string;
  location_id?: string;
  name: string;
  description?: string;
  address?: string;
  location_type: string;
  is_active: boolean;
  company_id?: string;
  created_at?: number;
  updated_at?: number;
}

@customElement('locations-page')
export class LocationsPage extends LitElement {
  @state() private locations: Location[] = [];
  @state() private isLoading = true;
  @state() private error: string | null = null;
  @state() private showAddModal = false;
  @state() private selectedLocation: Location | null = null;
  @state() private showDeleteModal = false;
  @state() private locationToDelete: Location | null = null;
  @state() private formData: { name: string; description: string; address: string; location_type: string; is_active: boolean } = { 
    name: '', 
    description: '', 
    address: '', 
    location_type: '', 
    is_active: true 
  };

  @property({ type: String }) companyId: string = '';
  @property({ type: Object }) firestore: Firestore = null as any;

  connectedCallback() {
    super.connectedCallback();
    this.loadData();
  }

  private async loadData() {
    try {
      this.isLoading = true;
      this.error = null;

      const locationsResult = await this.loadLocations();

      // Only set error if there's an actual failure, not just no data
      if (locationsResult === false) {
        this.error = 'Failed to load locations';
      } else {
        // Success - clear any previous errors
        this.error = null;
      }
    } catch (error) {
      console.error('Error loading data:', error);
      this.error = 'Failed to load data';
    } finally {
      this.isLoading = false;
    }
  }

  private async loadLocations(): Promise<boolean> {
    try {
      // Check if companyId is available
      if (!this.companyId) {
        console.warn('No company ID available, skipping locations load');
        this.locations = [];
        return true; // Not an error, just no data
      }
      
      const result = await getLocationsByCompany(this.firestore, this.companyId);
      
      if (result.data && result.data.length > 0) {
        // Transform the data to match the expected format
        this.locations = result.data.map((item: any) => ({
          id: item.id,
          location_id: item.model?.location_id || item.id,
          name: item.model?.name || item.name || 'Unknown Location',
          description: item.model?.description || null,
          address: item.model?.address || null,
          location_type: item.model?.location_type || 'Other',
          is_active: item.model?.is_active || false,
          company_id: item.model?.company_id,
          created_at: item.model?.created_at,
          updated_at: item.model?.updated_at,
        }));
        return true;
      } else {
        // No data is not an error - just set empty array
        this.locations = [];
        return true;
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      return false;
    }
  }



  private handleAddLocation = () => {
    // Reset form data before opening modal
    this.formData = { name: '', description: '', address: '', location_type: '', is_active: true };
    this.showAddModal = true;
  };

  private handleModalClosed() {
    this.showAddModal = false;
    this.formData = { name: '', description: '', address: '', location_type: '', is_active: true };
  }

  private handleLocationCreated(event: CustomEvent) {
    const newLocation = event.detail;
    // Add the new location to the local array immediately
    this.locations.push(newLocation);
    this.showAddModal = false;
    this.formData = { name: '', description: '', address: '', location_type: '', is_active: true };
    // Also refresh from server to ensure consistency
    this.loadData();
  }

  private handleLocationClick = (location: Location) => {
    this.selectedLocation = location;
  };

  private handleViewerClosed = () => {
    this.selectedLocation = null;
  };

  private handleDeleteClick = (location: Location) => {
    this.showDeleteModal = true;
    this.locationToDelete = location;
  };

  private handleDeleteConfirm() {
    if (this.locationToDelete) {
      this.handleDeleteLocation(this.locationToDelete);
    }
  }

  private handleDeleteCancel() {
    this.showDeleteModal = false;
    this.locationToDelete = null;
  }

  private async handleDeleteLocation(location: Location) {
    try {
      this.isLoading = true;
      
      // Call the API to delete the location from the database
      const result = await deleteLocation(this.firestore, location.id);
      
      if (!result.error) {
        // Location successfully deleted from database, now remove from local array
        const index = this.locations.findIndex(l => (l.id || l.location_id) === (location.id || location.location_id));
        if (index !== -1) {
          this.locations.splice(index, 1);
        }
        
        // Also close the location viewer if the deleted location was being viewed
        if (this.selectedLocation && (this.selectedLocation.id || this.selectedLocation.location_id) === (location.id || location.location_id)) {
          this.selectedLocation = null;
        }
        
        // Reset all modal states to ensure clean state
        this.showAddModal = false;
        
        // Close the delete modal
        this.showDeleteModal = false;
        this.locationToDelete = null;
      } else {
        // Handle deletion error
      }

    } catch (error) {
      // Error deleting location
    } finally {
      this.isLoading = false;
    }
  }


  render() {
    const tableConfig = createLocationsTableConfig(
      this.locations,
      {
        onRowClick: this.handleLocationClick,
        onDelete: this.handleDeleteClick,
        onAddLocation: this.handleAddLocation
      }
    );

    // Override loading and error state
    tableConfig.loading = this.isLoading;
    tableConfig.error = this.error;

    return html`
      <div class="locations-page">
        <data-table .config=${tableConfig}></data-table>

      <!-- Add Location Modal -->
      <location-modal
        .open=${this.showAddModal}
        .formData=${this.formData}
        .companyId=${this.companyId}
        .firestore=${this.firestore}
        @modal-closed=${this.handleModalClosed}
        @location-created=${this.handleLocationCreated}
      ></location-modal>

      <!-- Location Viewer Modal -->
      <location-viewer
        .open=${this.selectedLocation !== null}
        .location=${this.selectedLocation}
        .firestore=${this.firestore}
        @modal-closed=${this.handleViewerClosed}
      ></location-viewer>

      <!-- Delete Confirmation Modal -->
      <sl-dialog
        label="Delete Location"
        .open=${this.showDeleteModal}
      >
        <p>Are you sure you want to delete <strong>${this.locationToDelete?.name}</strong>?</p>
        <p style="color: var(--sl-color-neutral-600); font-size: 0.875rem;">
          This action cannot be undone. All associated data will be permanently removed.
        </p>

        <div slot="footer">
          <sl-button
            variant="neutral"
            size="large"
            @click=${this.handleDeleteCancel}
            ?disabled=${this.isLoading}
          >
            Cancel
          </sl-button>
          <sl-button
            variant="danger"
            size="large"
            @click=${this.handleDeleteConfirm}
            ?loading=${this.isLoading}
            ?disabled=${this.isLoading}
          >
            Delete
          </sl-button>
        </div>
      </sl-dialog>
      </div>
    `;
  }

  static styles = styles;
}
